<template>
  <li
    class="timeline-period"
    :class="`timeline-period--${period.displayPosition}`"
  >
    <div
      class="timeline-period__card timeline-period__card--interactive"
    >
      <button
        ref="cardElement"
        type="button"
        class="timeline-period__surface relative flex h-full w-full cursor-pointer overflow-hidden rounded-2xl border border-slate-200 bg-white text-left shadow-sm focus-visible:outline-none"
        @click="selectPeriod"
      >
        <img
          v-if="period.coverImage"
          :src="period.coverImage"
          :alt="`Representative memory from ${periodLabel}`"
          :height="period.coverImageHeight"
          :width="period.coverImageWidth"
          class="absolute inset-0 h-full w-full object-cover transition-transform duration-300"
        >
        <div v-if="period.coverImage" class="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />

        <div class="relative mt-auto w-full p-3 text-white xs:p-4 lg:p-5">
          <p class="font-sans text-2xl font-normal leading-none xs:text-3xl lg:text-4xl">
            {{ periodLabel }}
          </p>
          <p class="mt-2 text-base font-semibold opacity-85 lg:text-lg">
            {{ memoryCountLabel }}
          </p>
        </div>
      </button>

      <timeline-period-action-menu
        v-if="showActions"
        class="timeline-period__actions absolute bottom-3 right-3 z-20 transition-opacity xs:bottom-4 xs:right-4 lg:bottom-5 lg:right-5"
        :aria-label="`Actions for ${periodLabel}`"
        color="white"
        variant="solid"
        placement="top-end"
        frosted
        @change-cover="emitAction('change-cover')"
        @cover-designer="emitAction('cover-designer')"
        @declutter="emitAction('declutter')"
      />
    </div>

    <span class="timeline-period__connector bg-slate-300" aria-hidden="true" />
    <span class="timeline-period__marker rounded-full border-[3px] border-slate-400 bg-white transition-all" aria-hidden="true" />
    <span class="timeline-period__axis-label text-lg font-bold text-slate-400 transition-colors" aria-hidden="true">
      {{ periodLabel }}
    </span>
  </li>
</template>

<script setup>
  const props = defineProps({
    period: {
      type: Object,
      required: true
    },
    showActions: Boolean
  });

  const emit = defineEmits(['select', 'action']);
  const cardElement = ref(null);

  const periodLabel = computed(() => formatTimelinePeriodLabel(props.period));
  const memoryCountLabel = computed(() => formatTimelineMemoryCount(props.period.files_count));

  function selectPeriod() {
    emit('select', props.period);
  }

  function emitAction(action) {
    emit('action', {action, period: props.period});
  }

  function focus(options) {
    cardElement.value?.focus(options);
  }

  defineExpose({focus});
</script>

<style scoped>
  .timeline-period {
    position: relative;
    flex-shrink: 0;
    width: var(--timeline-period-width);
    height: var(--timeline-vertical-period-step);
  }

  .timeline-period__card {
    position: absolute;
    z-index: 10;
    top: 50%;
    width: var(--timeline-card-width);
    aspect-ratio: 4 / 3;
    transform: translateY(-50%);
  }

  .timeline-period--top .timeline-period__card {
    left: 0;
  }

  .timeline-period--bottom .timeline-period__card {
    right: 0;
  }

  .timeline-period__connector {
    position: absolute;
    top: var(--timeline-axis-position);
    height: 1px;
    width: var(--timeline-connector-gap);
    transition: background-color 180ms ease, height 180ms ease, width 180ms ease;
  }

  .timeline-period--top .timeline-period__connector {
    left: var(--timeline-card-width);
    right: 50%;
    width: auto;
  }

  .timeline-period--bottom .timeline-period__connector {
    left: 50%;
    right: var(--timeline-card-width);
    width: auto;
  }

  .timeline-period__marker {
    position: absolute;
    z-index: 20;
    left: 50%;
    top: var(--timeline-axis-position);
    height: 18px;
    width: 18px;
    transform: translate(-50%, -50%);
  }

  .timeline-period__axis-label {
    position: absolute;
    display: none;
  }

  .timeline-period__card--interactive:focus-within ~ .timeline-period__marker,
  .timeline-period__card--interactive:hover ~ .timeline-period__marker {
    height: 27px;
    width: 27px;
    border-color: var(--timeline-accent-color);
    box-shadow: 0 0 0 7px rgba(0, 113, 152, 0.14);
  }

  .timeline-period__card--interactive:focus-within ~ .timeline-period__connector,
  .timeline-period__card--interactive:hover ~ .timeline-period__connector {
    height: 2px;
    background-color: var(--timeline-accent-color);
  }

  .timeline-period__surface {
    isolation: isolate;
    transform: translateZ(0);
    backface-visibility: hidden;
    transition: border-color 180ms ease, box-shadow 180ms ease;
  }

  .timeline-period__card--interactive:hover .timeline-period__surface {
    border-color: var(--timeline-accent-color);
    box-shadow:
      0 0 0 1px var(--timeline-accent-color),
      0 14px 28px rgba(15, 23, 42, 0.14);
  }

  .timeline-period__card--interactive:focus-within .timeline-period__surface {
    border-color: var(--timeline-accent-color);
    box-shadow:
      0 0 0 1px var(--timeline-accent-color),
      0 0 0 5px rgba(0, 113, 152, 0.14);
  }

  .timeline-period__card--interactive:hover img {
    transform: scale(1.02);
  }

  .timeline-period__actions {
    opacity: 0.6;
  }

  @media (hover: hover) and (pointer: fine) {
    .timeline-period__actions {
      opacity: 0;
      pointer-events: none;
    }

    .timeline-period__card--interactive:hover .timeline-period__actions,
    .timeline-period__card--interactive:focus-within .timeline-period__actions {
      opacity: 1;
      pointer-events: auto;
    }
  }

  @media (min-width: 1200px) {
    .timeline-period {
      height: var(--timeline-track-height);
      padding: 0;
    }

    .timeline-period__card {
      left: 50%;
      right: auto;
      width: var(--timeline-card-width);
      height: var(--timeline-card-height);
      aspect-ratio: auto;
      transform: translateX(-50%);
    }

    .timeline-period--bottom .timeline-period__card {
      right: auto;
    }

    .timeline-period--top .timeline-period__card {
      left: 50%;
      right: auto;
      top: 0;
      bottom: auto;
    }

    .timeline-period--bottom .timeline-period__card {
      left: 50%;
      right: auto;
      top: auto;
      bottom: 0;
    }

    .timeline-period__connector {
      left: 50%;
      right: auto;
      width: 1px;
    }

    .timeline-period--top .timeline-period__connector,
    .timeline-period--bottom .timeline-period__connector {
      left: 50%;
      right: auto;
      width: 1px;
    }

    .timeline-period--top .timeline-period__connector {
      top: var(--timeline-card-height);
      bottom: var(--timeline-axis-position);
      height: auto;
    }

    .timeline-period--bottom .timeline-period__connector {
      top: var(--timeline-axis-position);
      bottom: var(--timeline-card-height);
      height: auto;
    }

    .timeline-period__card--interactive:focus-within ~ .timeline-period__connector,
    .timeline-period__card--interactive:hover ~ .timeline-period__connector {
      height: auto;
      width: 2px;
    }

    .timeline-period__marker {
      left: 50%;
      top: var(--timeline-axis-position);
    }

    .timeline-period__axis-label {
      display: block;
    }

    .timeline-period--top .timeline-period__axis-label {
      left: 50%;
      top: calc(var(--timeline-axis-position) + 30px);
      transform: translateX(-50%);
    }

    .timeline-period--bottom .timeline-period__axis-label {
      bottom: calc(var(--timeline-axis-position) + 30px);
      left: 50%;
      transform: translateX(-50%);
    }

    .timeline-period__card--interactive:focus-within ~ .timeline-period__axis-label,
    .timeline-period__card--interactive:hover ~ .timeline-period__axis-label {
      color: var(--timeline-accent-color);
    }

  }
</style>
